import React, { useState, useEffect } from 'react';
import { getReports } from '../reportStore';
import { generateDailyReports } from '../reportGenerator';
import { playOpen, playClose } from '../clickSound';

const btnBlue = { padding: '8px 16px', borderRadius: '6px', fontWeight: 700, fontSize: '13px', background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white' };

const Metric = ({ label, value, unit }) => (
  <div style={{ textAlign: 'center', padding: '8px 4px' }}>
    <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>{value}{unit || ''}</div>
    <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
  </div>
);

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [driverFilter, setDriverFilter] = useState('All');
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setReports(await getReports());
  useEffect(() => { refresh(); }, []);

  const drivers = ['All', ...Array.from(new Set(reports.map((r) => r.driverName)))];
  const visible = reports.filter((r) => driverFilter === 'All' || r.driverName === driverFilter);

  const genToday = async () => {
    setBusy(true);
    try { await generateDailyReports(new Date()); await refresh(); }
    catch (e) { alert(`Could not generate: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1>📄 Daily Driver Reports</h1>
          <p>End-of-day driving summary per unit, attached to the assigned driver. Auto-generated each day.</p>
        </div>
        <button style={btnBlue} onClick={genToday} disabled={busy}>{busy ? 'Generating…' : "Generate today's report"}</button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#64748b' }}>Driver:</span>
        <select value={driverFilter} onChange={(e) => setDriverFilter(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}>
          {drivers.map((d) => <option key={d}>{d}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {visible.map((r) => {
          const isOpen = open === r.id;
          return (
            <div key={r.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div onClick={() => { (isOpen ? playClose : playOpen)(); setOpen(isOpen ? null : r.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#0f172a' }}>{r.deviceName} <span style={{ color: '#94a3b8', fontWeight: 400 }}>· {r.day}</span></div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>👤 {r.driverName} · {r.metrics.distanceMi} mi · {r.metrics.idleMin} min idle</div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '999px', background: r.alertCount ? '#fffbeb' : '#f0fdf4', color: r.alertCount ? '#b45309' : '#15803d' }}>
                  {r.alertCount} alert{r.alertCount === 1 ? '' : 's'}
                </span>
                <span style={{ color: '#94a3b8', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
              </div>
              {isOpen && (
                <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 16px' }}>
                  <div className="grid grid-4" style={{ gap: '6px' }}>
                    <Metric label="Distance" value={r.metrics.distanceMi} unit=" mi" />
                    <Metric label="Avg speed" value={r.metrics.avgSpeedMph} unit=" mph" />
                    <Metric label="Max speed" value={r.metrics.maxSpeedMph} unit=" mph" />
                    <Metric label="Engine hrs" value={r.metrics.engineHours} unit=" h" />
                    <Metric label="Idle time" value={r.metrics.idleMin} unit=" min" />
                    {r.metrics.fuelUsed != null && <Metric label="Fuel used" value={r.metrics.fuelUsed} unit=" L" />}
                    {r.metrics.endOdo != null && <Metric label="Odometer" value={r.metrics.endOdo.toLocaleString()} unit=" mi" />}
                    <Metric label="Alerts" value={r.alertCount} />
                  </div>
                  {r.alertCount > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '4px' }}>ALERTS TRIGGERED</div>
                      {Object.entries(r.alertsByType).map(([type, n]) => (
                        <span key={type} style={{ display: 'inline-block', margin: '2px 6px 2px 0', fontSize: '12px', padding: '2px 10px', borderRadius: '999px', background: '#fef2f2', color: '#b91c1c', fontWeight: 600 }}>{type} × {n}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && <p className="text-muted">No reports yet. They generate automatically at the start of each new day (for the day before), or click “Generate today's report”.</p>}
      </div>

      <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '14px' }}>
        Driving figures come from the tracker's daily summary; idle and alert counts are tallied while the dashboard runs. On the server these will generate automatically every night.
      </p>
    </div>
  );
}
